import { useCallback, useEffect, useState } from 'react';
import { FiEdit2, FiPlay, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import { fetchAgents } from '../api/agentService';
import { useToast } from '../components/Toast';
import {
  createQueue,
  deleteQueue,
  dispatchQueue,
  fetchQueues,
  updateQueue,
} from '../api/queueService';

const STRATEGY_OPTIONS = [
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'manual', label: 'Manual' },
];

function QueueModal({ agents, onClose, onSaved, queue }) {
  const toast = useToast();
  const isEdit = Boolean(queue?.id);
  const [form, setForm] = useState({
    name: queue?.name || '',
    description: queue?.description || '',
    routing_strategy: queue?.routing_strategy || 'round_robin',
    sla_hours: queue?.sla_hours ?? 24,
    members: queue?.members || [],
    is_default: queue?.is_default ?? false,
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (event) => {
    const { checked, name, type, value } = event.target;
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const toggleMember = (agentId) => {
    setForm((current) => ({
      ...current,
      members: current.members.includes(agentId)
        ? current.members.filter((id) => id !== agentId)
        : [...current.members, agentId],
    }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        routing_strategy: form.routing_strategy,
        sla_hours: Number(form.sla_hours),
        members: form.members,
        member_agent_ids: form.members,
        is_default: form.is_default,
      };

      if (!payload.name) {
        throw new Error('Queue name is required.');
      }

      if (isEdit) {
        await updateQueue(queue.id, payload);
      } else {
        await createQueue(payload);
      }
      onSaved();
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.error || err?.response?.data?.message || 'Unable to save queue.');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit Queue' : 'Create Queue'}</h3>
          <button className="modal-close" type="button" onClick={onClose} aria-label="Close">x</button>
        </div>

        <form className="stack-form" onSubmit={onSubmit}>
          <label>
            <span>Name</span>
            <input name="name" value={form.name} onChange={onChange} />
          </label>

          <label>
            <span>Description</span>
            <textarea name="description" rows="3" value={form.description} onChange={onChange} />
          </label>

          <div className="form-row">
            <label>
              <span>Routing Strategy</span>
              <select name="routing_strategy" value={form.routing_strategy} onChange={onChange}>
                {STRATEGY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              <span>SLA Hours</span>
              <input name="sla_hours" type="number" min="1" value={form.sla_hours} onChange={onChange} />
            </label>
          </div>

          <div className="toggle-field">
            <span className="toggle-field-label">Default Queue</span>
            <label className="toggle-switch">
              <input name="is_default" type="checkbox" checked={form.is_default} onChange={onChange} />
              <span className="toggle-track"><span className="toggle-thumb" /></span>
            </label>
          </div>

          <div>
            <span style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>Members</span>
            <div style={{ display: 'grid', gap: 8, maxHeight: 180, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }}>
              {agents.map((agent) => (
                <label key={agent.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 500 }}>
                  <input
                    type="checkbox"
                    checked={form.members.includes(agent.id)}
                    onChange={() => toggleMember(agent.id)}
                  />
                  <span>{agent.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <button className="primary-btn" type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Queue'}
            </button>
            <button className="ghost-btn" type="button" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function QueuesPage() {
  const toast = useToast();
  const [queues, setQueues] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingQueue, setEditingQueue] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const loadPageData = useCallback(async () => {
    setLoading(true);
    try {
      const [queuesResponse, agentsResponse] = await Promise.all([
        fetchQueues(),
        fetchAgents(),
      ]);
      setQueues(queuesResponse.queues || queuesResponse.data?.queues || []);
      setAgents(agentsResponse.data || agentsResponse.agents || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Unable to load queues.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  const handleDelete = async (queueId) => {
    if (!window.confirm('Delete this queue?')) {
      return;
    }

    try {
      await deleteQueue(queueId);
      toast.success('Queue deleted.');
      await loadPageData();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Unable to delete queue.');
    }
  };

  const handleDispatch = async (queueId) => {
    try {
      const response = await dispatchQueue(queueId);
      toast.success(`Queue dispatched. ${response.dispatched || 0} leads assigned, ${response.failed || 0} failed.`);
      await loadPageData();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Unable to dispatch queue.');
    }
  };

  return (
    <div>
      <div className="page-title-row">
        <div>
          <h2>Lead Queues</h2>
          <p style={{ margin: '6px 0 0', color: '#64748b' }}>
            Manage fallback queues, SLA windows, memberships, and queue dispatch operations.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="ghost-btn" type="button" onClick={loadPageData}>
            <FiRefreshCw style={{ marginRight: 6 }} /> Refresh
          </button>
          <button className="primary-btn" type="button" onClick={() => { setEditingQueue(null); setShowModal(true); }}>
            New Queue
          </button>
        </div>
      </div>

      {loading ? <p>Loading queues…</p> : null}

      {!loading ? (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Strategy</th>
              <th>SLA</th>
              <th>Members</th>
              <th>Current Size</th>
              <th>Total Processed</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {queues.length ? (
              queues.map((queue) => (
                <tr key={queue.id}>
                  <td>
                    <strong style={{ color: '#0f172a' }}>{queue.name}</strong>
                    <div style={{ color: '#64748b', fontSize: 13 }}>
                      {queue.is_default ? 'Default queue' : 'Standard queue'}
                    </div>
                  </td>
                  <td>{STRATEGY_OPTIONS.find((option) => option.value === queue.routing_strategy)?.label || queue.routing_strategy}</td>
                  <td>{queue.sla_hours} hrs</td>
                  <td>{Array.isArray(queue.members) ? queue.members.length : 0}</td>
                  <td>{queue.current_size ?? 0}</td>
                  <td>{queue.total_processed ?? 0}</td>
                  <td>
                    <div className="table-actions">
                      <button className="icon-action-btn" type="button" title="Edit queue" onClick={() => { setEditingQueue(queue); setShowModal(true); }}>
                        <FiEdit2 />
                      </button>
                      <button className="icon-action-btn" type="button" title="Dispatch queue" onClick={() => handleDispatch(queue.id)}>
                        <FiPlay />
                      </button>
                      <button className="icon-action-btn icon-action-btn--danger" type="button" title="Delete queue" onClick={() => handleDelete(queue.id)}>
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="7">No queues found.</td></tr>
            )}
          </tbody>
        </table>
      ) : null}

      {showModal ? (
        <QueueModal
          agents={agents}
          queue={editingQueue}
          onClose={() => setShowModal(false)}
          onSaved={async () => {
            setShowModal(false);
            setEditingQueue(null);
            toast.success(editingQueue ? 'Queue updated.' : 'Queue created.');
            await loadPageData();
          }}
        />
      ) : null}
    </div>
  );
}

export default QueuesPage;
